import { getFilterOperator } from '@core/services/filter';
import BaseModel from '@core/model';

import { hash, uuid } from '@utils/string';

import { recordResponseSource } from '@/components/table/portfolioDetailReport';
import { createHeader } from '@/components/table/utils';

// In-flight requests by collection id. Not kept on the row: Vuex-ORM deep-clones a record on insert, which turns a
// Promise into a plain object that resolves at once.
const pending = new Map();

export class Collection extends BaseModel {
    static entity = 'collections';

    /**
     * Model object for the collection item.
     */
    static model = null;

    /**
     * Asynchronous function to get a list of records from the API.
     */
    static endpoint = null;

    /**
     * The type of the collection class.
     */
    static type = 'Collection';

    static fields() {
        return {
            _preventTransform: this.boolean(false),
            id: this.string(() => uuid()),
            offset: this.number(null).nullable(),
            limit: this.number(null).nullable(),
            sort_by: this.attr([]),
            filters: this.attr({}),
            config: this.attr({}),
            response: this.attr(null),
            item_ids: this.attr([]),
            type: this.string(null).nullable()
        };
    }

    has_model(model) {
        return this.constructor.model === model;
    }

    get cast() {
        return this.constructor.model;
    }

    get item() {
        return id => {
            if (!this.constructor.model) {
                throw new Error(`Collection model not set on collection class: '${this.constructor.type}'`);
            }

            return this.constructor.model.$get(id);
        };
    }

    get search() {
        return (criteria, filters = {}, abortGroup = null) => {
            if (!this.constructor.model) {
                throw new Error(`Collection model not set on collection class: '${this.constructor.type}'`);
            }

            if (abortGroup) {
                this.constructor.model.abortSearch(abortGroup);
            }

            return this.constructor.model.search(criteria, filters, { abortGroup });
        };
    }

    static async updateItemInCollections(itemId, newItemData) {
        const collections = Collection.query()
            .where('item_ids', ids => ids.includes(itemId))
            .get();

        for (const c8n of collections) {
            const index = c8n.response.data.findIndex(item => item.id === itemId);

            if (index > -1) {
                c8n.response.data.splice(index, 1, { ...c8n.response.data[`${index}`], ...newItemData });
            }
        }
    }

    static async removeItemInCollections(itemId) {
        const collections = Collection.query()
            .where('item_ids', ids => ids.includes(itemId))
            .get();

        for (const c8n of collections) {
            const index = c8n.response.data.findIndex(item => item.id === itemId);

            if (index > -1) {
                c8n.response.data.splice(index, 1);

                if (c8n.response.metadata.total <= c8n.response.data.length) {
                    c8n.response.metadata.total = c8n.response.data.length;
                } else {
                    c8n.response.metadata.total--;
                }
            }
        }
    }

    static async removeCollectionWithItem(itemId) {
        const collections = Collection.query()
            .where('item_ids', ids => ids.includes(itemId))
            .get();

        for (const c8n of collections) {
            Collection.delete(c8n.id);
        }
    }

    get headers() {
        return [];
    }

    get default_headers() {
        return [];
    }

    get header_groups() {
        return [];
    }

    get pdf_headers() {
        return this.default_headers || [];
    }

    get bulk_update_headers() {
        return [
            createHeader('control', '', {
                format: 'control',
                width: '10rem',
                align: 'left',
                persistent: true
            })
        ];
    }

    get preset_offset() {
        return null;
    }

    get preset_limit() {
        return null;
    }

    get preset_sort_by() {
        return [];
    }

    get preset_filters() {
        return {};
    }

    get preset_config() {
        return {};
    }

    /**
     * Get the collection data from the API.
     *
     * Offset - The number of records to skip.
     * Limit - The number of records to return.
     * SortBy - The field to sort by.
     * Filters - The filters to apply.
     * Config - The API configuration to apply.
     *
     * Where a preset value is not provided on the collection class, the value will be taken
     * from the collection instance. If the instance does not have the value, the value will be
     * taken from the arguments.
     */
    get request() {
        if (!this.constructor.endpoint) {
            return () =>
                Promise.resolve({
                    data: [],
                    metadata: {
                        total: 0
                    }
                });
        }

        return async (offset = null, limit = null, sort_by = [], filters = {}, config = {}) => {
            if (this.preset_offset !== null && this.preset_offset !== undefined) {
                offset = this.preset_offset;
            } else if (this.offset !== null && this.offset !== undefined) {
                offset = this.offset;
            }

            if (this.preset_limit !== null && this.preset_limit !== undefined) {
                limit = this.preset_limit;
            } else if (this.limit !== null && this.limit !== undefined) {
                limit = this.limit;
            }

            const incomingSortBy = Array.isArray(sort_by) ? sort_by : [];

            if (this.preset_sort_by && this.preset_sort_by.length > 0) {
                sort_by = [...this.preset_sort_by, ...incomingSortBy];
            } else if (this.sort_by && this.sort_by.length > 0) {
                sort_by = [...this.sort_by, ...incomingSortBy];
            } else {
                sort_by = incomingSortBy;
            }

            if (this.preset_filters && Object.keys(this.preset_filters).length > 0) {
                filters = { ...this.preset_filters, ...filters };
            } else if (this.filters && Object.keys(this.filters).length > 0) {
                filters = { ...this.filters, ...filters };
            }

            if (this.preset_config && Object.keys(this.preset_config).length > 0) {
                config = { ...this.preset_config, ...config };
            } else if (this.config && Object.keys(this.config).length > 0) {
                config = { ...this.config, ...config };
            }

            filters = this.transformFilters(filters);
            const id = hash([
                this.constructor.type,
                offset,
                limit,
                sort_by,
                filters,
                config,
                config.force ? uuid() : null
            ]);

            // Checked and registered before the first await, so an identical call in the same tick waits on this one
            let loading = pending.get(id);
            const cached = loading ? null : Collection.find(id);
            const source = loading ? 'in_flight' : cached ? 'cache' : 'network';

            if (source === 'network') {
                loading = (async () => {
                    const request = this.constructor.endpoint(offset, limit, sort_by, filters, config);

                    await Collection.insert({
                        data: {
                            id,
                            offset,
                            limit,
                            sort_by,
                            filters,
                            config,
                            type: this.constructor.type
                        }
                    });

                    // Covers on_complete too, so a failed transform cannot leave a row with no response
                    try {
                        const result = await request;
                        await this.on_complete(id, result);

                        return result;
                    } catch (error) {
                        await this.on_error(id);
                        throw error;
                    }
                })();

                pending.set(id, loading);
                loading.then(
                    () => pending.delete(id),
                    () => pending.delete(id)
                );
            }

            const response = await loading;
            // The cache can be cleared during the await; a cache hit keeps the row it found
            const found = Collection.find(id) || cached;

            if (found) {
                recordResponseSource(found.response, source);
                return found.response;
            }

            if (response && response.status === 'success') {
                recordResponseSource(response, source);
                return response;
            }
        };
    }

    transformFilters(filters = {}) {
        return filters;
    }

    createHeader(key, label, options = {}) {
        return createHeader(key, label, options);
    }

    filterOperator(operator) {
        return getFilterOperator(operator);
    }

    async transform_item(item) {
        return item;
    }

    async on_complete(id, response) {
        if (Array.isArray(response.data) && response.data.length > 0) {
            this.item_ids = response.data.map(item => item.id);
            if (!this._preventTransform) {
                response.data = await Promise.all(response.data.map(item => this.transform_item(item)));
            }
        }

        await Collection.update({
            where: id,
            data: { response, item_ids: this.item_ids }
        });
    }

    async on_error(id) {
        await Collection.delete(id);
    }

    /**
     * Clears every cached collection response, not only this instance's. It is the cache's only expiry: without it,
     * a response not invalidated by a write stays cached for the whole session.
     */
    $delete() {
        return Collection.deleteAll();
    }
}

export default Collection;
